<template>
    <div class="page__content availabilities-by-campsite" :class="{'is-sticky': sticky.isSticky, 'is-readonly': readOnly}">
        <div class="header" :style="headerStyle">
            <div class="columns" v-if="!sticky.isSticky">
                <div class="column is-8">
                    <h2 class="page__headline" v-if="!readOnly">{{ $t('availabilities.detail.headline') }}</h2>
                    <h2 class="page__headline" v-else>{{ $t('availabilities.detail.headlineReadOnly') }}</h2>
                    <div class="backlink">
                        <router-link :to="backLink" class="link">
                            <span @click="onBacklinkClicked()">
                                <span class="pre">&laquo;</span> <span class="main">{{ $t('generic.back') }}</span>
                            </span>
                        </router-link>
                    </div>
                </div>
            </div>

            <div class="columns is-multiline availabilities-filter-tools" v-if="!sticky.isSticky && dateFrom !== dateUntil">
                <availabilities-campsite-and-date-filter :campsites="campsites" :campsiteId="campsiteId" :month="mainMonth" @route-request="onNewRouteRequested($event)"></availabilities-campsite-and-date-filter>
            </div>

            <div class="columns is-multiline">
                <div class="column is-12" v-if="!sticky.isSticky && dateFrom !== dateUntil">
                    <div class="notification is-yellow" v-html="$t('availabilities.detail.notification')" v-if="!readOnly"></div>
                </div>
                <div class="column is-12" v-if="sticky.isSticky">
                    <div class="backlink">
                        <router-link :to="backLink" class="link">
                            <span class="pre">&laquo;</span> <span class="main">{{ $t('generic.back') }}</span>
                        </router-link>
                    </div>
                </div>
                <div class="column is-4">
                    <h3 id="campsite-name"><strong>{{ campsite.name }}</strong> ({{ $t('availabilities.detail.totalQuota', {quantity: campsite.quantity}) }})</h3>
                </div>

                <div class="column is-8 availability-filter">
                    <availability-percent-filter v-model="filter" :complex="true" @toggle="toggleFilter($event)" v-if="dateFrom !== dateUntil" trackName="AvailabilitiesByCampsite"></availability-percent-filter>
                </div>

                <div class="column is-4" v-if="!readOnly">
                    <div class="block" v-if="dateFrom !== dateUntil">
                        <button class="button is-primary can-flash" :class="{'is-loading': loading, 'is-disabled': hasUnsavedChanges || !someCheckboxesChecked, 'flash': bulkFlash}" @click.prevent="toggleBulkEdit(true)">{{ $t('availabilities.detail.editBulk') }}</button>
                    </div>
                </div>

                <div class="column is-8" v-if="!readOnly">
                    <div class="block is-pulled-right">
                        <button class="button is-warning" :class="{'is-loading': loading, 'is-disabled': !hasUnsavedChanges}" @click.prevent="discard()">{{ $t('availabilities.detail.discard') }}</button>
                        <button class="button is-primary" :class="{'is-loading': loading, 'is-disabled': !hasUnsavedChanges || hasErrors}" @click.prevent="save()">{{ $t('availabilities.detail.save') }}</button>
                    </div>
                </div>
            </div>

            <div class="columns is-multiline" v-if="sticky.isSticky">
                <div class="column is-12">
                    <table class="table header-table is-narrow">
                        <thead>
                            <tr>
                                <th class="is-narrow" v-if="!readOnly && dateFrom !== dateUntil">
                                    <input type="checkbox" v-model="allChecked" :disabled="hasUnsavedChanges">
                                </th>
                                <th class="date">
                                    {{ $t('availabilities.detail.date') }}
                                    <router-link :to="{ name: 'availabilities-by-campsite', params: { campsiteId: campsiteId }, query: getMoreDaysQuery(-1) }" class="button load-more-button up is-small" :class="{'is-loading': loading}" v-if="moreDaysAllowed(-1)">
                                        <span @click="onLoadMoreDaysClicked(-1)">
                                            <i class="icon arrow-icon fa fa-angle-up"></i> {{ $t('availabilities.detail.earlierDays') }}
                                        </span>
                                    </router-link>
                                </th>
                                <th class="plusminus">
                                    {{ $t('availabilities.detail.dailyQuota') }}
                                    <info-i :track="['AvailabilitiesByCampsite', 'dailyQuota']">
                                        <span v-if="readOnly">{{ $t('availabilities.flyouts.dailyQuotaReadOnly') }}</span>
                                        <span v-else>{{ $t('availabilities.flyouts.plusMinus') }}</span>
                                    </info-i>
                                </th>
                                <th class="has-text-centered">
                                    {{ $t('availabilities.detail.onlineBookings') }}
                                    <info-i v-if="!pms" :track="['AvailabilitiesByCampsite', 'bookings']">{{ $t('availabilities.flyouts.bookings') }}</info-i>
                                    <info-i v-else :track="['AvailabilitiesByCampsite', 'bookings']"><span v-html="$t('availabilities.flyouts.bookingsPMS')"></span></info-i>
                                </th>
                                <th class="has-text-centered">{{ $t('availabilities.detail.available') }} <info-i direction="left" :track="['AvailabilitiesByCampsite', 'availabilities']">{{ $t('availabilities.flyouts.availabilities') }}</info-i></th>
                            </tr>
                        </thead>
                        <tbody class="hidden-tbody">
                            <tr class="table-row-item">
                                <td class="is-narrow" v-if="!readOnly && dateFrom !== dateUntil"><div class="inner"><input type="checkbox"></div></td>
                                <td class="date">
                                    <span class="weekday">{{ getLatestDay() | localizedDate($t('generic.calendar.dateformatMomentjsWeekday')) }}</span>
                                    <span>{{ getLatestDay() | localizedDate($t('generic.calendar.dateformatMomentjsLong')) }}</span>
                                    <span class="tag is-small status-tag changed">{{ $t('availabilities.detail.changedInfo') }}</span>
                                </td>
                                <td class="plusminus">
                                    <div class="inner"><daily-quota :readOnly="readOnly" :max="1" step="1" :noFlyout="showBulkDoneOkay || showBulkDoneWarning"></daily-quota></div>
                                </td>
                                <td class="has-text-centered">
                                    <div class="inner">0</div>
                                </td>
                                <td class="has-text-centered">
                                    <div class="inner">
                                        <span class="result">1</span>
                                        <battery :isSmall="true"></battery>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        <div class="columns is-multiline">
            <div class="column is-12">
                <table class="table content-table is-narrow" :style="contentTableStyles">
                    <thead :class="{'is-invisible': sticky.isSticky}">
                        <tr>
                            <th class="is-narrow" v-if="!readOnly && dateFrom !== dateUntil"><input type="checkbox" v-model="allChecked" :disabled="hasUnsavedChanges"></th>
                            <th class="date">
                                {{ $t('availabilities.detail.date') }}
                                <router-link :to="{ name: 'availabilities-by-campsite', params: { campsiteId: campsiteId }, query: getMoreDaysQuery(-1) }" class="button load-more-button up is-small" :class="{'is-loading': loading}" v-if="moreDaysAllowed(-1)">
                                    <span @click="onLoadMoreDaysClicked(-1)">
                                        <i class="icon arrow-icon fa fa-angle-up"></i> {{ $t('availabilities.detail.earlierDays') }}
                                    </span>
                                </router-link>
                            </th>
                            <th class="plusminus">
                                {{ $t('availabilities.detail.dailyQuota') }}
                                <info-i :track="['AvailabilitiesByCampsite', 'dailyQuota']">
                                    <span v-if="readOnly">{{ $t('availabilities.flyouts.dailyQuotaReadOnly') }}</span>
                                    <span v-else>{{ $t('availabilities.flyouts.plusMinus') }}</span>
                                </info-i>
                            </th>
                            <th class="has-text-centered">
                                {{ $t('availabilities.detail.onlineBookings') }}
                                <info-i v-if="!pms" :track="['AvailabilitiesByCampsite', 'bookings']">{{ $t('availabilities.flyouts.bookings') }}</info-i>
                                <info-i v-else :track="['AvailabilitiesByCampsite', 'bookings']"><span v-html="$t('availabilities.flyouts.bookingsPMS')"></span></info-i>
                            </th>
                            <th class="has-text-centered">{{ $t('availabilities.detail.available') }} <info-i direction="left" :track="['AvailabilitiesByCampsite', 'availabilities']">{{ $t('availabilities.flyouts.availabilities') }}</info-i></th>
                        </tr>
                    </thead>
                    <tfoot v-if="dateFrom !== dateUntil">
                        <tr>
                            <th class="is-narrow"  v-if="!readOnly && dateFrom !== dateUntil"><input type="checkbox" v-model="allChecked" :disabled="hasUnsavedChanges"></th>
                            <th class="date">{{ $t('availabilities.detail.date') }}
                                <router-link :to="{ name: 'availabilities-by-campsite', params: { campsiteId: campsiteId }, query: getMoreDaysQuery(1) }" class="button load-more-button down is-small" :class="{'is-loading': loading}" v-if="moreDaysAllowed(1)">
                                    <span @click="onLoadMoreDaysClicked(1)">
                                        <i class="icon arrow-icon fa fa-angle-down"></i> {{ $t('availabilities.detail.laterDays') }}
                                    </span>
                                </router-link>
                            </th>

                            <th class="plusminus">{{ $t('availabilities.detail.dailyQuota') }}
                                <info-i :track="['AvailabilitiesByCampsite', 'dailyQuota']">
                                    <span v-if="readOnly">{{ $t('availabilities.flyouts.dailyQuotaReadOnly') }}</span>
                                    <span v-else>{{ $t('availabilities.flyouts.plusMinus') }}</span>
                                </info-i>
                            </th>
                            <th class="has-text-centered">
                                {{ $t('availabilities.detail.onlineBookings') }}
                                <info-i v-if="!pms" :track="['AvailabilitiesByCampsite', 'bookings']">{{ $t('availabilities.flyouts.bookings') }}</info-i>
                                <info-i v-else :track="['AvailabilitiesByCampsite', 'bookings']"><span v-html="$t('availabilities.flyouts.bookingsPMS')"></span></info-i>
                            </th>
                            <th class="has-text-centered">{{ $t('availabilities.detail.available') }} <info-i direction="left" :track="['AvailabilitiesByCampsite', 'availabilities']">{{ $t('availabilities.flyouts.availabilities') }}</info-i></th>
                        </tr>
                    </tfoot>
                    <tbody :name="'table-row'" is="transition-group">
                        <tr v-for="availability of availabilities" class="table-row-item" :class="{'is-tainted': availability.tainted, 'is-changed': availability.changed, 'is-blocked': availability.bookings > campsite.quantity}" v-if="shouldDisplay(availability)" :key="availability.idx">
                            <td class="is-narrow checkbox-td" v-if="!readOnly && dateFrom !== dateUntil" @click="onShowFlyoutRequested(availability)">
                                <input type="checkbox" v-model="availability.checked" :disabled="availability.changed || availability.bookings > campsite.quantity" @click="onCheckboxClicked(availability.checked)">
                                <info-i :showI="false" :shown="availability.flyoutShown" @hide="onHideFlyoutRequested(availability)">
                                    {{ $t('availabilities.flyouts.checkbox') }}
                                </info-i>
                            </td>
                            <td class="date">
                                <span class="weekday">{{ availability.day | localizedDate($t('generic.calendar.dateformatMomentjsWeekday')) }}</span>
                                <span>{{ availability.day | localizedDate($t('generic.calendar.dateformatMomentjsLong')) }}</span>
                                <span class="tag is-small status-tag tainted" v-if="!availability.changed && availability.tainted">{{ $t('availabilities.detail.taintedInfo') }}</span>
                                <span class="tag is-small status-tag changed" v-if="availability.changed">{{ $t('availabilities.detail.changedInfo') }}</span>
                            </td>
                            <td class="plusminus">
                                <daily-quota :readOnly="readOnly" :min="availability.bookings" :max="campsite.quantity" step="1" v-model="availability.dailyQuota" :disabled="availability.checked || availability.bookings > campsite.quantity" @input="onQuotaChanged(availability)" @error="onQuotaError(availability, $event)" :noFlyout="showBulkDoneOkay || showBulkDoneWarning" @flyoutShown="onDisabledQuotaClicked()"></daily-quota>
                                <info-i v-if="availability.bookings > campsite.quantity" infoStyle="warning" :track="['AvailabilitiesByCampsite', 'blockedWarning']">
                                    {{ $t('availabilities.flyouts.blockedRow') }}
                                </info-i>
                            </td>
                            <td class="has-text-centered">
                                <span>{{ availability.bookings }}</span>
                            </td>
                            <td>
                                <div class="columns">
                                    <div class="column is-6 has-text-right">
                                        <span class="result">{{ availability.result }}</span>
                                    </div>
                                    <div class="column is-6">
                                        <battery v-model="availability.percent" v-if="!loading" :isSmall="true"></battery>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <div class="modal bulk-editor" :class="{'is-active': showBulkEditor}">
            <div class="modal-background"></div>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">
                        {{ $t('availabilities.bulk.title') }}
                    </p>
                    <button class="delete" @click.prevent="toggleBulkEdit(false)"></button>
                    <div class="sub-title">{{ campsite.name }}</div>
                </header>
                <section class="modal-card-body">
                    <div>{{ $t('availabilities.bulk.selectedDays') }}</div>
                    <ul>
                        <li v-for="range of checkedDayRanges" v-if="bulkEditorShowMore || range[2] < 3">
                            <span v-if="range[0] === range[1]" class="tag is-small is-warning">{{ range[0] | localizedDate($t('generic.calendar.dateformatMomentjsLong')) }}</span>
                            <span v-else class="tag is-small is-warning">{{ range[0] | localizedDate($t('generic.calendar.dateformatMomentjsLong')) }} {{ $t('seasons.until') }} {{ range[1] | localizedDate($t('generic.calendar.dateformatMomentjsLong')) }}</span>
                        </li>
                        <li v-if="checkedDayRanges.length > 3">
                            <a @click.prevent="bulkEditorShowMore = !bulkEditorShowMore"><span v-if="bulkEditorShowMore">{{ $t('availabilities.bulk.less') }}</span><span v-else>{{ $t('availabilities.bulk.more') }}</span></a>
                        </li>
                    </ul>
                    <form @submit.prevent="acceptBulk()">
                        <div class="field">
                            <label class="label">{{ $t('availabilities.detail.dailyQuota') }}</label>
                            <div class="control">
                                <input class="input" type="number" min="0" :max="campsite.quantity" step="1" v-model="bulkValue" :class="{'is-danger': bulkValueIllegal}" />
                            </div>
                            <p class="notification is-yellow" v-html="$t('availabilities.bulk.hint')"></p>
                        </div>
                    </form>
                </section>
                <footer class="modal-card-foot">
                    <div class="block is-pulled-right">
                        <button class="button is-link" @click.prevent="toggleBulkEdit(false)">{{ $t('generic.cancel') }}</button>
                        <button class="button is-primary" @click.prevent="acceptBulk()" :class="{'is-disabled': bulkValue === null || bulkValue === undefined || bulkValue === '' || bulkValueIllegal}">{{ $t('availabilities.bulk.apply') }}</button>
                    </div>
                </footer>
            </div>
        </div>
        <div class="modal bulk-editor-done is-success" :class="{'is-active': showBulkDoneOkay}">
            <div class="modal-background" @click.prevent="showBulkDoneOkay = false"></div>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">
                        {{ $t('availabilities.bulk.notifications.default.title') }}
                    </p>
                    <button class="delete" @click.prevent="showBulkDoneOkay = false"></button>
                </header>
                <section class="modal-card-body">
                    <div class="left-right-wrapper">
                        <div class="left">
                            <i class="icon green fa fa-check-circle"></i>
                        </div>
                        <div class="right" v-html="$t('availabilities.bulk.notifications.default.body')"></div>
                    </div>
                </section>
                <footer class="modal-card-foot">
                    <button class="button is-primary" @click.prevent="showBulkDoneOkay = false">{{ $t('generic.close') }}</button>
            </footer>
            </div>
        </div>

        <div class="modal bulk-editor-done is-danger" :class="{'is-active': showBulkDoneWarning}">
            <div class="modal-background" @click.prevent="showBulkDoneWarning = false"></div>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">
                        {{ $t('availabilities.bulk.notifications.error.title') }}
                    </p>
                    <button class="delete" @click.prevent="showBulkDoneWarning = false"></button>
                </header>
                <section class="modal-card-body">
                    <div class="left-right-wrapper">
                        <div class="left">
                            <i class="icon yellow fa fa-exclamation-triangle"></i>
                        </div>
                        <div class="right">
                            <div v-html="$t('availabilities.bulk.notifications.error.body')"></div>
                            <div class="notification is-yellow" v-html="$t('availabilities.bulk.notifications.error.subbody')"></div>
                        </div>
                    </div>
                </section>
                <footer class="modal-card-foot">
                    <button class="button is-primary" @click.prevent="showBulkDoneWarning = false">{{ $t('availabilities.bulk.notifications.error.button') }}</button>
            </footer>
            </div>
        </div>

        <div class="modal before-route-leave is-success" :class="{'is-active': beforeRouteLeaveModalActive}">
            <div class="modal-background"></div>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">
                        {{ $t('availabilities.detail.leaveNotification.title') }}
                    </p>
                </header>
                <section class="modal-card-body">
                    <div class="left-right-wrapper">
                        <div class="left">
                            <i class="icon yellow fa fa-exclamation-triangle"></i>
                        </div>
                        <div class="right">
                            <div v-html="$t('availabilities.detail.leaveNotification.body')"></div>
                        </div>
                    </div>
                </section>
                <footer class="modal-card-foot">
                    <div class="block">
                        <button class="button is-link" @click.prevent="hideBeforeRouteLeaveModal(false)">{{ $t('availabilities.detail.leaveNotification.ctaCancel') }}</button>
                        <button class="button is-primary" @click.prevent="hideBeforeRouteLeaveModal(true)">{{ $t('availabilities.detail.leaveNotification.ctaForce') }}</button>
                    </div>
            </footer>
            </div>
        </div>
    </div>
</template>

<script>
    import { Api } from '../../../api';
    import { AppModel } from '../../../models';
    import filters from '../../../filters';
    import { EventBus, Browser, Tracking } from '../../../utils';
    import { Battery, DailyQuota, InfoI, AvailabilityPercentFilter, AvailabilitiesCampsiteAndDateFilter } from '../../components';
    import { cloneDeep } from 'lodash';
    import moment from 'moment';

    const getDateFromAndUntil = function(query) {
        const now = new Date();
        const start = moment(`${now.getFullYear()}-${now.getMonth() + 1}-1`, 'YYYY-M-D');
        let dateFrom = query.date_from || null;
        let dateUntil = query.date_until || null;

        if(!dateFrom || !moment(dateFrom, 'YYYY-MM-DD').isValid()) {
            dateFrom = start.format('YYYY-MM-DD');
        }
        if(!dateUntil || !moment(dateUntil, 'YYYY-MM-DD').isValid()) {
            const end = moment(start).add(1, 'months').subtract(1, 'days');
            dateUntil = end.format('YYYY-MM-DD');
        }

        return [dateFrom, dateUntil];
    };

    export default {
        name: 'availabilities-by-campsite',

        components: {
            Battery,
            AvailabilityPercentFilter,
            AvailabilitiesCampsiteAndDateFilter,
            DailyQuota,
            InfoI,
        },

        props: {
            campsiteId: {
                type: Number,
                'default': null,
            },
        },

        filters: filters,

        data() {
            return {
                readOnly: true,
                pms: false,
                loading: true,
                dateFrom: null,
                dateUntil: null,
                campsites: [],
                campsite: {},
                availabilities: [],
                availabilitiesFromServer: [],
                iCameFrom: null,
                filter: [true, true, true, true, false],
                headerStyle: '',
                contentTableStyles: {},
                allChecked: false,
                showBulkEditor: false,
                bulkEditorShowMore: false,
                bulkValue: null,
                showBulkDoneOkay: false,
                showBulkDoneWarning: false,
                beforeRouteLeaveModalActive: false,
                nextFunc: null,
                cancelFunc: null,
                nextLogout: false,
                bulkFlash: false,
                bulkFlashTimeout: null,
                sticky: {
                    onResize: null,
                    onScroll: null,
                    isSticky: false,
                    style: '',
                    initialScroll: null,
                },
            };
        },

        created() {
            [this.dateFrom, this.dateUntil] = getDateFromAndUntil(this.$route.query);
            this.sticky.onScroll = this._onScroll.bind(this);
            this.sticky.onResize = this._onResize.bind(this);
            this.addListeners();
            this.sticky.onResize();
        },

        beforeDestroy() {
            this.removeListeners();
        },

        beforeRouteLeave(to, f, next) {
            if(this.hasUnsavedChanges) {
                this.showBeforeRouteLeaveModal(next, to.name === 'logout');
            } else {
                next();
            }
        },

        beforeRouteEnter(to, f, next) {
            const [dateFrom, dateUntil] = getDateFromAndUntil(to.query);

            Api.bootstrap().then(function(response) {
                return Promise.all([response, Api.availabilities.fetchAvailabilities({
                    'date_from': dateFrom,
                    'date_until': dateUntil,
                    'campsite': to.params.campsiteId,
                }), Api.campsites.fetchCampsites()]);
            }).then(function(response) {
                next((vm) => {
                    if(f.name === 'availabilities-by-month') {
                        vm.iCameFrom = f.fullPath;
                    }

                    if(response[1].status === 400) {
                        vm.$router.replace(vm.backLink);
                        return;
                    }

                    vm.loading = false;
                    vm.campsites = response[2].body;
                    for(let campsite of vm.campsites) {
                        if(campsite.id === vm.campsiteId) {
                            vm.campsite = campsite;
                        }
                    }
                    vm.pms = AppModel.activeCampground.settings.pms_api_active;
                    vm.readOnly = vm.pms || vm.campsite.quantity === 0;
                    vm.assignAvailabilities(response[1]);
                });
            });
        },

        beforeRouteUpdate(to, f, next) {
            this.loading = true;
            const [dateFrom, dateUntil] = getDateFromAndUntil(to.query);
            const isDirectionDown = dateUntil !== this.dateUntil && dateFrom === this.dateFrom;
            let scrollInterval = null;

            for(let campsite of this.campsites) {
                if(campsite.id === to.params.campsiteId) {
                    this.campsite = campsite;
                }
            }

            if(isDirectionDown) {
                scrollInterval = setInterval(() => {
                    window.scrollTo(0, 9999999);
                }, 1);
            }
            Api.availabilities.fetchAvailabilities({
                'date_from': dateFrom,
                'date_until': dateUntil,
                'campsite': to.params.campsiteId,
            }).then((response) => {
                next();

                this.dateFrom = dateFrom;
                this.dateUntil = dateUntil;
                this.assignAvailabilities(response);
                this.loading = false;
                if(isDirectionDown) {
                    setTimeout(() => {
                        clearInterval(scrollInterval);
                    }, 100);
                }
            }).catch(result => {
                this.loading = false;
                clearInterval(scrollInterval);
                console.error('Could not load availabilities!', result);
            });
        },

        computed: {
            backLink() {
                return this.iCameFrom || { name: 'availabilities-redirect', params: { lang: AppModel.lang } };
            },
            changedAvailabilities() {
                return this.availabilities.filter(a => a.changed).map(a => {
                    return {
                        count: a.dailyQuota,
                        campsite: a.campsite,
                        day: a.day,
                    };
                });
            },

            hasUnsavedChanges() {
                return this.changedAvailabilities.length > 0;
            },

            someCheckboxesChecked() {
                return this.availabilities.filter(a => a.checked).length > 0;
            },

            hasErrors() {
                return this.availabilities.filter(a => a.hasError).length > 0;
            },

            checkedDayRanges() {
                let ranges = [];
                let range = [];
                for(let avail of this.availabilities) {
                    if(avail.checked && this.shouldDisplay(avail)) {
                        if(!range.length) {
                            range = [avail.day, avail.day];
                        } else {
                            range[1] = avail.day;
                        }
                    } else {
                        if(range.length) {
                            range.push(ranges.length);
                            ranges.push(range);
                            range = [];
                        }
                    }
                }
                if(range.length) {
                    range.push(ranges.length);
                    ranges.push(range);
                }
                return ranges;
            },

            bulkValueIllegal() {
                return this.bulkValue < 0 || this.bulkValue > this.campsite.quantity;
            },

            mainMonth() {
                const start = moment(this.dateFrom, 'YYYY-MM-DD').toDate();
                const end = moment(this.dateUntil, 'YYYY-MM-DD').toDate();

                const midpoint = new Date((start.getTime() + end.getTime()) / 2);
                return new Date(midpoint.getFullYear(), midpoint.getMonth(), 1);
            },
        },

        watch: {
            allChecked(newVal, oldVal) {
                this.toggleAllChecked(newVal);
                if(newVal !== oldVal) {
                    setTimeout(() => {
                        if(!this.showBulkDoneOkay && !this.showBulkDoneWarning) {
                            Tracking.trackEvent('AvailabilitiesByCampsite', 'allCheckboxesClicked', newVal ? 'on' : 'off');
                        }
                    }, 10);
                }
            },
        },

        methods: {
            assignAvailabilities(response) {
                let idx = 0;
                const oldAvailabilities = cloneDeep(this.availabilities);
                this.availabilities = response.body.map((a) => {
                    let found = false;
                    for(let oldAvail of oldAvailabilities) {
                        if(oldAvail.day === a.day && oldAvail.campsite === a.campsite) {
                            Object.assign(a, oldAvail);
                            found = true;
                            break;
                        }
                    }
                    a.idx = idx++;
                    if(!found) {
                        a.dailyQuota = a.availability;
                        a.tainted = true;
                        a.changed = false;
                        a.checked = false;
                        a.flyoutShown = false;
                        a.hasError = false;
                        if(a.dailyQuota === null || a.dailyQuota === undefined) {
                            a.dailyQuota = a.quantity;
                            a.tainted = false;
                        } else if(a.dailyQuota > a.quantity) {
                            a.dailyQuota = a.quantity;
                        }
                        a.result = Math.max(0, a.dailyQuota - a.bookings);

                        if(a.quantity > 0) {
                            a.percent = Math.round(a.result * 10000.0 / a.quantity) / 100;
                        } else {
                            a.percent = 0;
                        }
                        a.dailyQuotaFromServer = a.dailyQuota;
                    }
                    return a;
                });

                this.availabilitiesFromServer = cloneDeep(this.availabilities);

                for(let avail of this.availabilities) {
                    for(let oldAvail of oldAvailabilities) {
                        if(oldAvail.day === avail.day && oldAvail.campsite === avail.campsite) {
                            avail.tainted = oldAvail.tainted;
                            avail.checked = oldAvail.checked;
                            avail.flyoutShown = false;
                            avail.changed = oldAvail.changed;
                            avail.hasError = oldAvail.hasError;
                            avail.dailyQuota = oldAvail.dailyQuota;
                        }
                    }
                }
            },

            onQuotaChanged(availability) {
                availability.changed = availability.dailyQuotaFromServer !== availability.dailyQuota;
                availability.result = Math.max(0, availability.dailyQuota - availability.bookings);
                if(availability.quantity > 0) {
                    availability.percent = Math.round(availability.result * 10000.0 / availability.quantity) / 100;
                } else {
                    availability.percent = 0;
                }
            },

            shouldDisplay(availability) {
                if(availability.changed) {
                    return true;
                }

                let idx = 0;
                if(availability.percent <= 50 && availability.percent > 20) {
                    idx = 1;
                } else if(availability.percent <= 20 && availability.percent > 0) {
                    idx = 2;
                } else if(availability.percent === 0) {
                    idx = 3;
                }

                let additionalCondition = true;
                if(this.filter[4] === true) {
                    additionalCondition = availability.tainted;
                }
                return this.filter[idx] && additionalCondition;
            },

            discard() {
                this.availabilities = cloneDeep(this.availabilitiesFromServer);
                Tracking.trackEvent('AvailabilitiesByCampsite', 'discardButtonClicked');
            },

            save() {
                this.loading = true;
                Tracking.trackEvent('AvailabilitiesByCampsite', 'saveButtonClicked');
                Api.availabilities.updateAvailabilities(this.changedAvailabilities).then((response) => {
                    this.onAvailabilitiesUpdated(response.body);
                    this.loading = false;
                }, (response) => {
                    console.error(response);
                    this.loading = false;
                });
            },

            onAvailabilitiesUpdated(availabilities) {
                for(let availability of availabilities) {
                    this.updateAvailability(availability);
                }

                EventBus.$emit(EventBus.SHOW_NOTIFICATION, { title: `<h3>${this.$tc('availabilities.notification.headline', availabilities.length, {count: availabilities.length})}</h3>`, direction: 'Up', body: this.$tc('availabilities.notification.body', availabilities.length, {count: availabilities.length}) });
            },

            updateAvailability(availability) {
                for(let i in this.availabilities) {
                    if(availability.day === this.availabilities[i].day && availability.campsite === this.availabilities[i].campsite) {
                        this.availabilities[i].tainted = availability.action !== 'deleted';
                        this.availabilities[i].dailyQuota = availability.count;
                        this.availabilities[i].changed = false;
                        this.availabilities[i].result = Math.max(0, this.availabilities[i].dailyQuota - this.availabilities[i].bookings);
                        if(this.availabilities[i].quantity > 0) {
                            this.availabilities[i].percent = Math.round(this.availabilities[i].result * 10000.0 / this.availabilities[i].quantity) / 100;
                        } else {
                            this.availabilities[i].percent = 0;
                        }
                        this.availabilities[i].dailyQuotaFromServer = this.availabilities[i].dailyQuota;
                    }
                }
            },

            getMoreDaysQuery(direction) {
                let start = moment(this.dateFrom, 'YYYY-MM-DD');
                let end = moment(this.dateUntil, 'YYYY-MM-DD');
                if(direction === -1) {
                    start.add(-7, 'days');
                } else {
                    end.add(7, 'days');
                }

                return {
                    date_from: start.format('YYYY-MM-DD'),
                    date_until: end.format('YYYY-MM-DD')
                };
            },

            moreDaysAllowed(direction) {
                if(this.dateFrom === this.dateUntil) {
                    return false;
                }
                const mainMonthStart = moment(this.mainMonth);
                const mainMonthEnd = moment(new Date(this.mainMonth.getFullYear(), this.mainMonth.getMonth() + 1, 0));
                const start = moment(this.dateFrom, 'YYYY-MM-DD');
                const end = moment(this.dateUntil, 'YYYY-MM-DD');
                let diff;
                if(direction === -1) {
                    diff = mainMonthStart.diff(start, 'days');
                } else {
                    diff = end.diff(mainMonthEnd, 'days');
                }
                return diff < 21;
            },

            loadMoreDays(direction) {
                let start = moment(this.dateFrom, 'YYYY-MM-DD');
                let end = moment(this.dateUntil, 'YYYY-MM-DD');
                if(direction === -1) {
                    start.add(-7, 'days');
                } else {
                    end.add(7, 'days');
                }
            },

            onQuotaError(availability, error) {
                availability.hasError = error;
            },

            addListeners() {
                this.removeListeners();
                if(Browser.isIE()) {
                    return;
                }
                window.addEventListener('scroll', this.sticky.onScroll);
                window.addEventListener('resize', this.sticky.onResize);
            },
            removeListeners() {
                window.removeEventListener('scroll', this.sticky.onScroll);
                window.removeEventListener('resize', this.sticky.onResize);
            },
            _onScroll() {
                const top = document.getElementById('campsite-name').getBoundingClientRect().top;
                if(top <= 80) {
                    if(this.sticky.initialScroll === null) {
                        this.sticky.initialScroll = window.scrollY;
                    }
                    this.setSticky();
                }

                if(this.sticky.initialScroll !== null && window.scrollY < this.sticky.initialScroll) {
                    this.unsetSticky();
                }
            },
            _onResize() {
                if(this.$el === undefined) {
                    setTimeout(() => {
                        this._onResize();
                    }, 10);
                    return;
                }
                this.headerStyle = {
                    width: Math.min(window.innerWidth - 50, this.$el.getElementsByClassName('content-table')[0].offsetWidth) + 'px',
                };
            },
            setSticky() {
                if(Browser.isIE()) {
                    return;
                }
                if(this.sticky.isSticky) {
                    return;
                }
                this.sticky.isSticky = true;
                this.setPaddingTop();
            },
            unsetSticky() {
                this.sticky.isSticky = false;
                this.setPaddingTop();
            },
            toggleFilter(open = null) {
                this.setPaddingTop();
            },
            setPaddingTop(final = false) {
                if(Browser.isIE()) {
                    return;
                }
                if(!this.sticky.isSticky) {
                    this.contentTableStyles = {};
                } else {
                    let h = this.$el.getElementsByClassName('header')[0].getBoundingClientRect().height + 50;
                    if(this.sticky.initialScroll !== null) {
                        h += this.sticky.initialScroll - 112;
                    }
                    this.contentTableStyles = {
                        'margin-top': h + 'px',
                    };
                }
                if(!final) {
                    setTimeout(() => {
                        this.setPaddingTop(true);
                    }, 1);
                }
            },
            getLatestDay() {
                if(this.availabilities && this.availabilities.length) {
                    return this.availabilities[this.availabilities.length - 1].day;
                }
                return '2000-12-31';
            },
            toggleAllChecked(checked = true) {
                for(let availability of this.availabilities) {
                    if(availability.bookings <= this.campsite.quantity) {
                        availability.checked = checked;
                    }
                }
            },

            toggleBulkEdit(on = true, track = true) {
                this.showBulkEditor = on;
                if(track) {
                    if(on) {
                        Tracking.trackEvent('AvailabilitiesByCampsite', 'bulkEditButtonClicked');
                    } else {
                        Tracking.trackEvent('AvailabilitiesByCampsite', 'bulkEditorCanceled');
                    }
                }
            },

            acceptBulk() {
                for(let availability of this.availabilities.filter(a => a.checked && this.shouldDisplay(a))) {
                    availability.changed = availability.dailyQuota !== this.bulkValue;
                    availability.dailyQuota = this.bulkValue;
                    availability.checked = false;
                }
                this.allChecked = false;
                this.toggleBulkEdit(false, false);

                Tracking.trackEvent('AvailabilitiesByCampsite', 'bulkEditorAcceptClicked');

                setTimeout(() => {
                    this.showBulkDoneOkay = !this.hasErrors;
                    this.showBulkDoneWarning = this.hasErrors;
                }, 1);
            },

            showBeforeRouteLeaveModal(nextFunc, isLogout = false) {
                this.beforeRouteLeaveModalActive = true;
                this.nextLogout = isLogout;
                this.nextFunc = nextFunc;
            },

            hideBeforeRouteLeaveModal(next = false) {
                this.beforeRouteLeaveModalActive = false;
                if(next && this.nextFunc) {
                    if(this.nextLogout) {
                        Api.user.logout();
                        this.$router.push({name: 'login'});
                    }
                    this.nextFunc();
                }
                if(!next && this.cancelFunc) {
                    this.cancelFunc();
                    this.cancelFunc = null;
                }
            },

            onNewRouteRequested(data) {
                if(this.hasUnsavedChanges) {
                    this.cancelFunc = data.cancelCallback;
                    this.showBeforeRouteLeaveModal(() => {
                        this.$router.replace(data.route);
                    });
                } else {
                    this.$router.push(data.route);
                }
            },
            onHideFlyoutRequested(availability) {
                if(availability.changed) {
                    availability.flyoutShown = false;
                }
            },
            onShowFlyoutRequested(availability) {
                if(availability.changed) {
                    availability.flyoutShown = true;
                    Tracking.trackEvent('AvailabilitiesByCampsite', 'disabledCheckboxClicked');
                }
            },

            onBacklinkClicked() {
                Tracking.trackEvent('AvailabilitiesByCampsite', 'backLinkClicked');
            },

            onCheckboxClicked(on) {
                Tracking.trackEvent('AvailabilitiesByCampsite', 'checkboxClicked', on ? 'on' : 'off');
            },

            onLoadMoreDaysClicked(direction) {
                Tracking.trackEvent('AvailabilitiesByCampsite', 'loadMoreDaysClicked', direction === -1 ? 'earlier' : 'later');
            },

            onDisabledQuotaClicked() {
                this.bulkFlash = true;
                clearTimeout(this.bulkFlashTimeout);
                this.bulkFlashTimeout = setTimeout(() => {
                    this.bulkFlash = false;
                    this.bulkFlashTimeout = setTimeout(() => {
                        this.bulkFlash = true;
                        this.bulkFlashTimeout = setTimeout(() => {
                            this.bulkFlash = false;
                        }, 300);
                    }, 200);
                }, 300);
            },
        },
    };
</script>

<style lang="scss">
    @import './availabilities-by-campsite.scss';
</style>



// WEBPACK FOOTER //
// src/views/pages/availabilities/availabilities-by-campsite.vue